import type { TemplateData } from '@/store/interstitial/store';
import { INTERSTITIAL_GRADIENTS } from '@/components/ui/interstitial/constants';

const headlineGradientStyle = {
  background: INTERSTITIAL_GRADIENTS.multiTestimonialHeadline,
  backgroundClip: 'text' as const,
  WebkitBackgroundClip: 'text' as const,
  WebkitTextFillColor: 'transparent' as const,
};

// Default interstitial data for direct URL access
export const DEFAULT_INTERSTITIAL_DATA: Record<string, TemplateData> = {
  statistics: {
    type: 'statistics',
    data: {
      headline: (
        <h1 className="text-[32px] leading-[38px] tracking-[-0.48px] mb-24 desktop:text-[71px] desktop:text-left desktop:leading-[82px] desktop:tracking-[-1.278px] desktop:mb-[48px] desktop:pt-[40px]">
          <span className="font-bold">85%</span> of men feel
          <br className="desktop:hidden" /> more confident in <br className="desktop:hidden" />{' '}
          their performance*
        </h1>
      ),
      imageUrl: '/assets/images/interstitials/statistics.jpg',
      disclaimer: (
        <div className="text-center desktop:text-left font-inter text-[12px] desktop:text-[22px] font-normal leading-[16px] desktop:leading-[28px] text-[var(--color-ashgray-soft,rgba(48,38,33,0.70))]">
          *Based on a survey of 114 active Rugiet Ready
          <br /> patients, conducted in May 2025.
        </div>
      ),
    },
  },
  'statistics-alt': {
    type: 'statistics-alt',
    data: {
      headline: (
        <h1 className="text-[32px] leading-[38px] tracking-[-0.48px] mb-24 desktop:text-[71px] desktop:text-left desktop:leading-[82px] desktop:tracking-[-1.278px] desktop:mb-[48px] desktop:pt-[40px]">
          <span className="font-bold">80%</span> of men prefer <br className="desktop:hidden" />{' '}
          Rugiet over other
          <br className="desktop:hidden" /> ED treatments*
        </h1>
      ),
      imageUrl: '/assets/images/interstitials/statistics-alt.jpg',
      disclaimer: (
        <div className="text-center desktop:text-left font-inter text-[12px] desktop:text-[22px] font-normal leading-[16px] desktop:leading-[28px] text-[var(--color-ashgray-soft,rgba(48,38,33,0.70))]">
          *Based on a survey of 114 active Rugiet Ready
          <br /> patients, conducted in May 2025.
        </div>
      ),
    },
  },

  'linear-progress': {
    type: 'linear-progress',
    data: {
      percent: 100,
      headline: (
        <h2
          className="mb-24 text-center font-inter text-[32px] font-normal leading-[36px] tracking-[-0.32px] desktop:mb-[44px] desktop:text-[72px] desktop:leading-[80px] desktop:tracking-[-1.296px]"
          style={{
            background: INTERSTITIAL_GRADIENTS.linearProgressHeadline,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          Tailoring your <br /> recommendation...
        </h2>
      ),
      supportingText: (
        <div className="text-center text-white text-base font-normal opacity-90">
          We've already helped over <span className="font-bold">150,000 men </span>
          bring out their best in the bedroom.
        </div>
      ),
    },
  },
  'results-loader': {
    type: 'results-loader',
    data: {
      test: 'test data',
    },
  },
  'welcome-steps': {
    type: 'welcome-steps',
    data: {
      title: 'Getting started',
      steps: [
        {
          title: 'Tell us about your health',
          shortDescription: 'Answer a few simple questions in our secure online medical questionnaire.',
        },
        {
          title: 'Create a personalized plan',
          shortDescription:
            'Our board-certified doctors will review your info and tailor a treatment for you.',
        },
        {
          title: 'Receive your medication',
          shortDescription:
            'We ship your personalized order directly to your door in discreet packaging.',
        },
      ],
    },
  },
  'circular-progress': {
    type: 'circular-progress',
    data: {
      percent: 100,
      subtext: (
        <>
          <h2
            className="font-inter font-normal text-[32px] leading-[40px] tracking-[-0.32px] text-center mb-12 desktop:mb-20 mt-64 desktop:text-[72px] desktop:leading-[80px] desktop:tracking-[-1.296px]"
            style={{
              background:
                'linear-gradient(92deg, rgba(239, 238, 238, 0.90) 0.91%, rgba(255, 253, 250, 0.90) 49.71%, rgba(255, 253, 252, 0.90) 65.04%, rgba(236, 236, 236, 0.90) 104.56%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Let's take your sex <br className="desktop:hidden" />
            life <br className="hidden desktop:block" />{' '}
            <span className="font-bold">to the next level.</span>
          </h2>
          <p className="font-inter font-normal text-[16px] leading-[22px] text-center text-[rgba(218,217,216,0.90)] desktop:text-[28px] desktop:leading-[36px] desktop:tracking-[-0.14px]">
            First, answer a few questions to tailor <br className="desktop:hidden" /> your
            treatment.
          </p>
        </>
      ),
    },
  },
  'circular-progress-with-welcome-steps': {
    type: 'circular-progress-with-welcome-steps',
    data: {
      percent: 100,
      subtext: (
        <>
          <h2
            className="font-inter font-normal text-[32px] leading-[40px] tracking-[-0.32px] text-center mb-12 desktop:mb-20 mt-64 desktop:text-[72px] desktop:leading-[80px] desktop:tracking-[-1.296px]"
            style={{
              background:
                'linear-gradient(92deg, rgba(239, 238, 238, 0.90) 0.91%, rgba(255, 253, 250, 0.90) 49.71%, rgba(255, 253, 252, 0.90) 65.04%, rgba(236, 236, 236, 0.90) 104.56%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Let's take your sex <br className="desktop:hidden" />
            life <br className="hidden desktop:block" />{' '}
            <span className="font-bold">to the next level.</span>
          </h2>
          <p className="font-inter font-normal text-[16px] leading-[22px] text-center text-[rgba(218,217,216,0.90)] desktop:text-[28px] desktop:leading-[36px] desktop:tracking-[-0.14px]">
            First, answer a few questions to tailor <br className="desktop:hidden" /> your
            treatment.
          </p>
        </>
      ),
    },
  },
  progress: {
    type: 'progress',
    data: {
      percent: 75,
      description: 'Loading your personalized experience...',
    },
  },
  'multi-testimonial': {
    type: 'multi-testimonial',
    data: {
      headline: (
        <h1
          style={{
            ...headlineGradientStyle,
            textShadow: '0px 0px 10px rgba(0, 0, 0, 0.08)',
          }}
          className="text-center font-inter text-[36px] desktop:text-[72px] font-normal leading-[42px] tracking-[-0.54px] mb-24 desktop:mb-32 desktop:py-24"
        >
          Trusted by <br className="desktop:hidden" />
          <span className="font-bold">300,000+ men</span>
        </h1>
      ),
      testimonials: [
        {
          stars: 5,
          quote:
            'I have tried about 5 different ED meds in the last 3 months, and to be honest, Rugiet blows them all away!!!!',
          author: 'Jed',
        },
        {
          stars: 5,
          quote:
            "Best ED med on the market. Hands down... works better than anything else I've tried.",
          author: 'Ryan',
        },
      ],
      buttonLabel: 'Continue',
    },
  },
  'performance-chart': {
    type: 'performance-chart',
    data: {
      headline: (
        <div className="text-center font-inter mb-16 text-[36px] font-normal leading-[42px] tracking-[-0.54px] desktop:text-[71px] desktop:leading-[82px] desktop:tracking-[-1.278px] desktop:mb-48 desktop:text-left">
          Get hard in{'\n'}
          just <span className="font-bold">15 minutes*</span>
        </div>
      ),
      disclaimer: (
        <div className="text-left font-inter text-[10px] font-normal leading-[14px] text-[var(--ash-gray-soft,rgba(48,38,33,0.70))] desktop:text-left desktop:text-[22px] desktop:leading-[28px]">
          *On average, after medication dissolves. Based on an internal survey of 108 active Rugiet
          patients conducted in May of 2025.
        </div>
      ),
      buttonLabel: 'Continue',
      imageUrl: '/assets/images/interstitials/graph.png',
    },
  },
  'product-info': {
    type: 'product-info',
    data: {
      headline: (
        <div className="text-center font-inter text-[36px] font-normal leading-[42px] tracking-[-0.54px] desktop:text-left desktop:text-[72px] desktop:leading-[82px] desktop:tracking-[-1.278px]">
          Meet <span className="font-bold">Ready</span>
        </div>
      ),
      subtitle: (
        <div className="text-center font-inter text-[19px] font-normal leading-[24px] tracking-[-0.19px] mb-24 text-silver desktop:text-left desktop:text-[35px] desktop:leading-[43px] desktop:tracking-[-0.525px]">
          Our best-selling ED treatment <br /> chosen by{' '}
          <span className="font-bold">300,000+ men</span>
        </div>
      ),
      disclaimer: (
        <div className="text-center font-inter text-[10px] font-normal leading-[16px] text-[var(--ash-gray-soft,rgba(48,38,33,0.70))] desktop:text-left desktop:text-[20px] desktop:leading-[26px]">
          *On average, after medication dissolves. Based on an internal survey of 108 active Rugiet
          patients conducted in May of 2025.
        </div>
      ),
      buttonLabel: 'Continue',
      imageUrl: '/assets/images/interstitials/ready.png',
    },
  },
  'product-formulation': {
    type: 'product-formulation',
    data: {
      headline: (
        <div className="text-center mb-20 desktop:mb-0 font-inter text-[32px] font-normal leading-[38px] tracking-[-0.48px] desktop:text-left desktop:text-[72px] desktop:leading-[82px] desktop:tracking-[-1.296px]">
          A winning formulation <br className="desktop:hidden" />
          for the bedroom
        </div>
      ),
      imageUrl: '/assets/images/interstitials/formulation.png',
      desktopImageUrl: '/assets/images/interstitials/formulation-desktop.png', // desktop
      disclaimer: (
        <div className="text-center font-inter text-[10px] font-normal leading-[14px] text-[var(--ash-gray-soft,rgba(48,38,33,0.70))] desktop:text-left desktop:text-[20px] desktop:leading-[26px]">
          *Based on a{' '}
          <a
            href="https://www.valueinhealthjournal.com/article/S1098-3015(12)01101-1/fulltext"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            study
          </a>{' '}
          of 50 men with ED, comparing patient preference of sublingual apomorphine and sildenafil
          versus sildenafil alone.
        </div>
      ),
    },
  },
  doctor: {
    type: 'doctor',
    data: {
      headline: (
        <h1 className="mb-16 desktop:mb-0 text-center text-[32px] leading-[38px] desktop:text-left desktop:text-[72px] desktop:leading-[82px] desktop:tracking-[-1.296px]">
          Answer a few{'\n'} questions about{'\n'}
          your health.
        </h1>
      ),
      disclaimer: (
        <h2 className="text-center font-inter text-[16px] font-normal leading-[22px] text-[var(--ash-gray-soft,rgba(48,38,33,0.70))] whitespace-pre-line desktop:text-left desktop:text-[24px] desktop:leading-[32px] desktop:tracking-[-0.36px] desktop:mb-auto">
          Our board-certified doctors use the <br className="desktop:hidden" />
          information in the following questions <br className="desktop:hidden" />
          to tailor your treatment.
        </h2>
      ),
      imageUrl: '/assets/images/interstitials/doctor.png',
      desktopImageUrl: '/assets/images/interstitials/doctor-desktop.png',
      doctorName: 'Vipul Khanpara',
      doctorTitle: 'M.D., Chief Medical Officer',
    },
  },
  'state-availability': {
    type: 'state-availability',
    data: {
      stateName: 'Texas',
      headline: <div>Rugiet is available in Texas</div>,
      subheadline: <div>Our clinicians are licensed and available to ship in your state</div>,
      shippingInfo: (
        <>
          within <span className="font-bold">48 hours</span>
        </>
      ),
      buttonLabel: 'Continue',
      imageUrl: '/assets/images/interstitials/texas.png',
    },
  },
  'sleep-stats': {
    type: 'sleep-stats',
    data: {
      headline: (
        <div className="text-center mb-20 desktop:mb-0 font-inter text-[32px] font-normal leading-[38px] tracking-[-0.48px] desktop:text-left desktop:text-[72px] desktop:leading-[82px] desktop:tracking-[-1.296px]">
          You aren't alone.
        </div>
      ),
      imageUrl: '/assets/images/interstitials/sleep-stats.png',
      desktopImageUrl: '/assets/images/interstitials/sleep-stats.png',
    },
  },
} as const;

// Helper function to get default data for a specific type
export const getDefaultInterstitialData = (type: string): TemplateData | null => {
  return DEFAULT_INTERSTITIAL_DATA[type] || null;
};

// Helper function to create state availability data for any state
export const createStateAvailabilityData = (stateName: string): TemplateData => ({
  type: 'state-availability',
  data: {
    stateName,
    shippingInfo: (
      <>
        within <span className="font-bold">48 hours</span>
      </>
    ),
    buttonLabel: 'Continue',
    imageUrl: `/assets/images/interstitials/${stateName.toLowerCase()}.png`,
  },
});

// List of available interstitial types
export const AVAILABLE_INTERSTITIAL_TYPES = Object.keys(DEFAULT_INTERSTITIAL_DATA);
